import type React from 'react';
import { useState } from 'react';
import { InlineEditableText } from '@components/InlineEditableText';
import InputText from '@components/InputText';
import { Show, ShowFirstMatching } from '@components/Show';
import { TooltipedIcon } from '@components/TooltipedIcon';
import { TOOLBAR_HEIGHT } from '@ContractBuilder/settings';
import { mdiAlertCircleOutline } from '@mdi/js';
import { withAmendedPostfix } from '@root/helpers/blocks';
import { cn } from 'cn';
import { twMerge } from 'tailwind-merge';

import { useBlockPanelContent } from '../hooks/useBlockPanelContent';

interface DrawerBlockNameProps {
  className?: string;
  /** Shows the system-managed `(amended)` postfix after the name in display mode only. */
  isAmended?: boolean;
  isBlockType: boolean;
  isDisabled?: boolean;
  name?: string;
  onNameChange: (name: string) => void;
}

interface DrawerBlockNameState {
  edit: boolean;
  value?: string;
}

export const DrawerBlockName = ({
  className,
  isAmended = false,
  isBlockType,
  isDisabled,
  name,
  onNameChange,
}: DrawerBlockNameProps) => {
  const { getContent } = useBlockPanelContent(isBlockType);
  const [state, setState] = useState<DrawerBlockNameState>({ edit: false, value: name });

  const NOT_EMPTY = getContent('fields.name.emptyError');

  const handleClick = () => {
    setState((s) => ({ ...s, edit: true }));
  };

  const handleCancel = () => {
    setState({ edit: false, value: name });
  };

  const handleConfirm = () => {
    onNameChange(state.value ?? '');
    setState((s) => ({ ...s, edit: false }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setState((s) => ({ ...s, value }));
  };

  const error = state.value?.trim() ? null : NOT_EMPTY;

  return (
    <div
      className={twMerge(
        '-mx-5',
        '-mt-[21px]',
        'px-5',
        'flex',
        'items-center',
        'justify-between',
        TOOLBAR_HEIGHT,
        className,
      )}
    >
      <div className="w-full">
        <InlineEditableText
          buttonDataTestId="block-name-inline-edit-icon"
          editor={
            <InputText
              value={state.value}
              onChange={handleChange}
              name="name"
              isDisabled={false}
              inputClassName={cn(error && 'border-error-500! pl-6 focus:outline-error-500')}
              placeholder={getContent('fields.name.placeholder')}
              startAdornment={
                <Show when={!!error}>
                  <div className="text-error-500 absolute top-3 left-1.5">
                    <TooltipedIcon
                      content={NOT_EMPTY}
                      dataTestId="block-name-error-icon"
                      path={mdiAlertCircleOutline}
                      className="text-error-500"
                      placement="left"
                    />
                  </div>
                </Show>
              }
            />
          }
          editMode={state.edit}
          disabled={isDisabled}
          display={
            <span
              className="relative w-11/12 overflow-hidden text-ellipsis whitespace-nowrap"
              data-testid="block-name-display-value"
            >
              <ShowFirstMatching>
                <Show when={!!state.value}>{withAmendedPostfix(state.value, isAmended)}</Show>
                <Show when={!state.value}>
                  <div className="flex items-center gap-1">
                    <div className="text-error-500">
                      <TooltipedIcon
                        content={NOT_EMPTY}
                        path={mdiAlertCircleOutline}
                        className="text-error-500"
                        placement="left"
                      />
                    </div>
                    <span className="text-info-300">{getContent('fields.name.placeholder')}</span>
                  </div>
                </Show>
              </ShowFirstMatching>
            </span>
          }
          onClick={handleClick}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          confirmDisabled={!!error}
        />
      </div>
    </div>
  );
};
